import React, { useEffect, useState } from 'react';
import classname from 'classnames';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import { LinkElement } from '@els/els-react--link-element';
import { useDispatch } from 'react-redux';
import { Banner } from '@els/els-react--banner';
import NodeRenderer from '../NodeRenderer';
import useFetchOperatorData from './hooks/useFetchOperator';
import useFetchBoundOps from './hooks/useFetchBoundOps';
import { addToBuilderAction } from '../QueryletActions';
import { useTargetMode } from '../TargetModeContext';

import './QueryPanel.scss';

function QueryPanel({ nodesList, onToggleQuerylet }) {
  const { operators } = useFetchOperatorData();
  const { boundOps } = useFetchBoundOps();
  const dispatch = useDispatch();
  const { active: isTargetModeActive, cancelTargetMode } = useTargetMode();
  const [isFormNode, setIsFormNode] = useState(false);
  const formInfoText =
    'Completing all fields in the form is not necessary. Please ensure that at least one field is filled to start your search.';

  const [, dropRef] = useDrop({
    accept: 'DragNode',
    drop: (item, monitor) => {
      if (monitor.didDrop()) return;
      dispatch(addToBuilderAction(item.node, 'default'));
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  useEffect(() => {
    const checkIfForm = nodesList.filter((node) => node?.trackingSearchType?.[0] === 'forms');
    setIsFormNode(checkIfForm.length > 0);
  }, [nodesList]);

  return (
    <main id="main-content" className={classname('query-builder__container', { 'not-empty': nodesList.length > 0 })} ref={dropRef}>
      {nodesList.length === 0 && (
        <div className="query-builder__properties__placeholder">
          <div className="query-builder__properties__placeholder__no-title">
            <h2 className="u-els-margin-bottom">There are no queries yet.</h2>
            <p className="u-els-margin-bottom-1o2">
              To build a new query, select fields and/or forms from the right panel by clicking on them or by dragging and dropping them
              here.
            </p>
            <LinkElement
              href="https://service.elsevier.com/app/answers/detail/a_id/11661/supporthub/reaxys/kw/query+builder/"
              isExternal
              iconSprite="UpRight"
              data-e2e="query-builder-learn-more-link"
              iconRight>
              Learn More
            </LinkElement>
          </div>
        </div>
      )}

      {nodesList.length > 0 && boundOps?.data && operators && (
        <div className="query-builder__properties drop-place">
          <Banner
            type="info"
            size="small"
            isVisible={isTargetModeActive}
            hideCloseLabel
            onClose={cancelTargetMode}
            className="query-builder__target-mode-banner u-els-margin-right-1o2">
            Tab to move between grouping targets. Enter to group, or Esc to cancel.
          </Banner>
          {!isTargetModeActive && isFormNode && (
            <Banner
              sprite="InformationOutlineCircle"
              isVisible
              size="small"
              onClose={() => setIsFormNode(false)}
              className="query-builder__form-info-banner">
              {formInfoText}
            </Banner>
          )}
          {nodesList.map((property, index) => (
            <div className="ql__container" key={`querypanel-${property.uuid}`}>
              <NodeRenderer
                index={index}
                node={property}
                boundOps={boundOps}
                nodesList={nodesList}
                onToggleQuerylet={onToggleQuerylet}
                type="Node"
                operators={operators}
              />
            </div>
          ))}
        </div>
      )}
    </main>
  );
}

QueryPanel.propTypes = {
  nodesList: PropTypes.array,
  onToggleQuerylet: PropTypes.func,
};

export default QueryPanel;
